import { useState, useEffect, useCallback, useMemo } from "react";
import { getCrewCalendar } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { CalendarDayCell, CalendarDayDetail } from "@/components/calendar/CalendarComponents";

const CREW_COLORS = [
  "bg-amber-500", "bg-blue-500", "bg-emerald-500", "bg-purple-500",
  "bg-rose-500", "bg-cyan-500", "bg-orange-500", "bg-indigo-500",
  "bg-teal-500", "bg-pink-500",
];

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatMonth(month) {
  const [y, m] = month.split("-");
  return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
}

function shiftMonth(month, delta) {
  const [y, m] = month.split("-");
  const d = new Date(parseInt(y), parseInt(m) - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function CrewCalendarView() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCrewCalendar(month);
      setData(res.data);
    } catch (err) { toast.error("Failed to load crew calendar"); }
    setLoading(false);
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const crewColorMap = useMemo(() => {
    if (!data?.crew) return {};
    const map = {};
    data.crew.forEach((c, i) => { map[c.crew_id] = CREW_COLORS[i % CREW_COLORS.length]; });
    return map;
  }, [data?.crew]);

  const crewNameMap = useMemo(() => {
    if (!data?.crew) return {};
    const map = {};
    data.crew.forEach(c => { map[c.crew_id] = c.name; });
    return map;
  }, [data?.crew]);

  const calendarDays = useMemo(() => {
    if (!data) return [];
    const [y, m] = month.split("-").map(Number);
    const startDow = new Date(y, m - 1, 1).getDay();
    const daysInMonth = new Date(y, m, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    return cells;
  }, [month, data]);

  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  if (loading && !data) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="crew-calendar-view" className="space-y-4">
      <div className="flex items-center justify-between">
        <Button data-testid="cal-prev-month" variant="ghost" size="sm" onClick={() => { setMonth(shiftMonth(month, -1)); setSelectedDay(null); }}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h2 className="text-lg font-bold uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{formatMonth(month)}</h2>
        <Button data-testid="cal-next-month" variant="ghost" size="sm" onClick={() => { setMonth(shiftMonth(month, 1)); setSelectedDay(null); }}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {(data?.crew || []).map(c => (
          <div key={c.crew_id} className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-full ${crewColorMap[c.crew_id]}`} />
            <span className="text-xs text-muted-foreground">{c.name}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <div className="grid grid-cols-7 gap-px mb-px">
            {DAY_NAMES.map(d => (
              <div key={d} className="text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground py-2">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px bg-border/30 border border-border rounded-sm overflow-hidden">
            {calendarDays.map((dateStr, i) =>
              !dateStr ? <div key={`empty-${i}`} className="bg-card/50 min-h-[100px]" /> : (
                <CalendarDayCell
                  key={dateStr}
                  dateStr={dateStr}
                  bookings={data?.days?.[dateStr] || []}
                  isToday={dateStr === todayStr}
                  isSelected={dateStr === selectedDay}
                  crewColorMap={crewColorMap}
                  crewNameMap={crewNameMap}
                  onClick={() => setSelectedDay(dateStr === selectedDay ? null : dateStr)}
                />
              )
            )}
          </div>
        </div>

        {selectedDay && (
          <CalendarDayDetail
            selectedDay={selectedDay}
            data={data}
            crewColorMap={crewColorMap}
            crewNameMap={crewNameMap}
            onClose={() => setSelectedDay(null)}
          />
        )}
      </div>
    </div>
  );
}
